import { useEffect, useState } from 'react';
import { useAppStore } from '../../store/useAppStore.js';
import { uploadGuideDocument } from '../../lib/guideDocuments.js';
import { StageFeedback } from './ReviewWaitingScreen.js';
import { FileDropZone } from './FileDropZone.js';
import { CamOnIcon, DocumentIcon } from '../../icons.js';

const MAX_VIDEO_BYTES  = 200 * 1024 * 1024;
const MAX_RESUME_BYTES = 10 * 1024 * 1024;

function videoRefusal(file: File): string | null {
  if (!/\.(mp4|mov)$/i.test(file.name)) return 'Please upload an MP4 or MOV video file.';
  if (file.size > MAX_VIDEO_BYTES)      return 'Your video is larger than 200 MB.';
  return null;
}

function resumeRefusal(file: File): string | null {
  if (!/\.(pdf|doc|docx)$/i.test(file.name)) return 'Your résumé must be a PDF, DOC, or DOCX file.';
  if (file.size > MAX_RESUME_BYTES)          return 'Your résumé is larger than 10 MB.';
  return null;
}

/**
 * A file released outside a drop zone would otherwise open in this tab, and the
 * guide would lose the screen and anything already chosen. The zones run first
 * and mark the events they handle, so this only catches the misses.
 */
function useIgnoreStrayFileDrops() {
  useEffect(() => {
    function onDragOver(e: DragEvent) {
      if (e.defaultPrevented) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'none';
    }
    function onDrop(e: DragEvent) { e.preventDefault(); }
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('drop', onDrop);
    };
  }, []);
}

export function VideoUploadScreen() {
  const stageFeedback = useAppStore((s) => s.stageFeedback);
  const refreshProfile = useAppStore((s) => s.refreshProfile);
  const [video, setVideo] = useState<File | null>(null);
  const [resume, setResume] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  useIgnoreStrayFileDrops();

  async function submit() {
    if (!video || !resume) return;
    setBusy(true); setErr(null);
    try {
      // Résumé first: it is far smaller, so a validation or auth failure shows
      // up before the guide waits out a 200 MB video upload. The stage only
      // advances once both refs are set, so the order is otherwise irrelevant.
      await uploadGuideDocument('resume', resume);
      await uploadGuideDocument('video', video);
      setDone(true);
      await refreshProfile();
    } catch (e: any) { setErr(e.message ?? String(e)); }
    finally { setBusy(false); }
  }

  if (done) return (
    <div className="max-w-md mx-auto card p-7 lg:p-10 has-grain animate-fade-up text-center">
      <h2 className="h-card mb-2">Thank you!</h2>
      <p className="hint">We received your video and résumé. A reviewer will look at both — you'll hear back in 1–5 business days.</p>
    </div>
  );

  return (
    <div className="max-w-md mx-auto card p-7 lg:p-10 has-grain animate-fade-up">
      <h2 className="h-card mb-2">Submit your application</h2>
      <p className="hint">Two things to upload: a short introduction video and your résumé.</p>

      <h3 className="mt-4 font-medium text-[0.95rem]">Introduction video</h3>
      <p className="mt-1 text-[0.85rem] text-ink-600 dark:text-ink-300">Keep it to 1 minute or less, in ASL. Please include:</p>
      <ul className="mt-1 list-disc pl-5 text-[0.85rem] text-ink-600 dark:text-ink-300 space-y-1">
        <li>Your name</li>
        <li>Where you are from</li>
        <li>Why you want to be a Deaf Guide</li>
        <li>Confirmation that you agree to the Code of Conduct</li>
        <li>Your approach to supporting new ASL learners</li>
      </ul>
      <p className="mt-2 text-[0.78rem] text-ink-500">Accepted: MP4 or MOV, max 200 MB.</p>
      <FileDropZone label="Introduction video file" noun="your video" icon={CamOnIcon} accept="video/mp4,video/quicktime,.mp4,.mov"
                    file={video} disabled={busy} validate={videoRefusal}
                    onFile={(f) => { setVideo(f); setErr(null); }} />

      <h3 className="mt-5 font-medium text-[0.95rem]">Résumé</h3>
      <p className="mt-1 text-[0.78rem] text-ink-500">Accepted: PDF, DOC, or DOCX, max 10 MB.</p>
      <FileDropZone label="Résumé file" noun="your résumé" icon={DocumentIcon} accept=".pdf,.doc,.docx,application/pdf"
                    file={resume} disabled={busy} validate={resumeRefusal}
                    onFile={(f) => { setResume(f); setErr(null); }} />

      <StageFeedback note={stageFeedback} />
      {err ? <p className="text-status-error dark:text-red-400 text-[0.85rem] mt-2">{err}</p> : null}
      <button type="button" disabled={!video || !resume || busy} onClick={() => void submit()}
              className="btn-primary mt-4">{busy ? 'Uploading…' : 'Submit application'}</button>
    </div>
  );
}
